import { createFileRoute, Link } from "@tanstack/react-router";
import { getMakes } from "@/lib/vehicle-queries";
import { breadcrumbLd, faqLd, SITE_OG_IMAGE, pageTitle } from "@/lib/seo";
import { FaqBlock } from "@/components/FaqBlock";

const PATH = "https://autospecdata.com/transmission-fluid-change-interval";
const TITLE = "Transmission Fluid Change Interval by Transmission Type";
const ANSWER =
  "Change automatic transmission fluid every 60,000 miles, CVT fluid every 30,000 to 40,000 miles, manual gear oil every 60,000 miles, and dual-clutch fluid every 40,000 miles. Halve every figure if you tow, idle in traffic, or drive in sustained heat — fluid degrades on heat cycles, not odometer readings.";

/**
 * Intervals below mirror the per-model schedule generator in src/lib/maintenance.ts,
 * so this hub and the ~2,900 model maintenance pages never contradict each other.
 */
const INTERVALS = [
  {
    type: "Conventional automatic (6–10 speed)",
    interval: "60,000 miles",
    severe: "30,000 miles",
    cost: "$150–$350",
    note: "The most forgiving of the four. Fluid is rated for the life of the unit by several manufacturers, which in practice means the life of the warranty. A drain-and-fill exchanges roughly 40% of the volume; the torque converter holds the rest.",
  },
  {
    type: "CVT (belt and pulley)",
    interval: "30,000–40,000 miles",
    severe: "25,000 miles",
    cost: "$180–$400",
    note: "The one interval not to stretch. A CVT transmits torque through a steel belt clamped by fluid pressure, so degraded fluid means belt slip, and belt slip means replacement rather than repair. Use the exact specified fluid — generic CVT fluid is a common failure cause.",
  },
  {
    type: "Manual",
    interval: "60,000 miles",
    severe: "30,000 miles",
    cost: "$110–$240",
    note: "Cheapest service of the four because it is a drain and refill with no filter or pan gasket. Gear oil in a manual shears down rather than burning; the symptom is notchy shifts when cold, not slipping.",
  },
  {
    type: "Dual-clutch (DCT / DSG)",
    interval: "40,000 miles",
    severe: "20,000 miles",
    cost: "$350–$700",
    note: "Wet-clutch units share fluid with the clutch packs, so the fluid carries friction material. Highest service cost of the four and the least tolerant of deferral. Dry-clutch DCTs use a smaller gearbox-only fill and cost less.",
  },
];

const SIGNS = [
  ["Delayed engagement", "One to two seconds between selecting Drive and the car moving. Low pressure from degraded or low fluid."],
  ["Shudder under light throttle", "Usually torque converter lockup on worn fluid. Frequently fixed by a fluid exchange if caught early."],
  ["Hard 1-2 shift when cold", "Fluid has lost viscosity control. Common at 80,000+ miles on a never-serviced automatic."],
  ["Burnt smell on the dipstick", "Fluid has overheated. A fluid change at this point is a gamble, not a fix."],
  ["Flare between gears", "Engine RPM rises without acceleration. Clutch pack slip — past the point fluid alone solves."],
];

const FAQS = [
  { q: "How often should transmission fluid be changed?", a: ANSWER },
  {
    q: "Is lifetime transmission fluid real?",
    a: "No, in the sense drivers assume. 'Fill for life' means the fluid is expected to last the design life the manufacturer warranties, commonly 100,000 miles. Transmissions that get fluid at 60,000 miles routinely outlast ones that never do, and the service costs $150–$350 against a $3,500–$7,000 replacement.",
  },
  {
    q: "Does towing change the interval?",
    a: "Yes, and it is the single biggest factor. Towing raises fluid temperature 30–50°F, and oxidation rate roughly doubles for every 20°F above 175°F. Halve the interval, and fit an auxiliary cooler if you tow above 50% of the rated capacity regularly.",
  },
  {
    q: "Flush or drain-and-fill?",
    a: "Drain-and-fill on any transmission with unknown history or over 100,000 unserviced miles. It exchanges about 40% of the volume with no pressure applied. A pressurised flush exchanges nearly all of it, which is better fluid but can dislodge deposits in a neglected unit.",
  },
  {
    q: "What happens if you never change it?",
    a: "Automatics typically fail between 120,000 and 180,000 miles on original fluid instead of clearing 200,000. CVTs are less forgiving — untouched fluid is the most commonly cited cause of CVT failure, and the failure mode is unit replacement.",
  },
];

export const Route = createFileRoute("/transmission-fluid-change-interval")({
  loader: async () => await getMakes(),
  component: TransmissionFluidHub,
  head: () => ({
    meta: [
      { title: pageTitle(TITLE) },
      { name: "description", content: ANSWER.slice(0, 158) },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: ANSWER.slice(0, 158) },
      { property: "og:url", content: PATH },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: SITE_OG_IMAGE },
      { name: "twitter:image", content: SITE_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: PATH }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          url: PATH,
          description: ANSWER,
          inLanguage: "en-US",
          isPartOf: { "@type": "WebSite", name: "AutoSpecData", url: "https://autospecdata.com/" },
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "[data-quick-answer]"] },
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqLd(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Transmission Fluid Change Interval", path: PATH },
          ]),
        ),
      },
    ],
  }),
});

function TransmissionFluidHub() {
  const { makes } = Route.useLoaderData();

  return (
    <div>
      <nav className="mx-auto max-w-6xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Transmission Fluid Change Interval</span>
      </nav>

      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            How Often to Change Transmission Fluid
          </h1>
          <p
            data-quick-answer
            data-aeo="atomic-passage"
            className="mt-4 max-w-3xl text-lg text-muted-foreground"
          >
            {ANSWER}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        <h2 className="text-2xl font-bold text-foreground">Interval and cost by transmission</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Transmission</th>
                <th className="py-2 pr-4 font-medium">Normal use</th>
                <th className="py-2 pr-4 font-medium">Severe use</th>
                <th className="py-2 pr-4 font-medium">Typical cost</th>
              </tr>
            </thead>
            <tbody>
              {INTERVALS.map((row) => (
                <tr key={row.type} className="border-b border-border/60 align-top">
                  <td className="py-3 pr-4 font-medium text-foreground">{row.type}</td>
                  <td className="py-3 pr-4 text-foreground">{row.interval}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{row.severe}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{row.cost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {INTERVALS.map((row) => (
            <div key={row.type} className="rounded-lg border border-border p-4">
              <h3 className="font-heading text-base font-semibold text-foreground">{row.type}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{row.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-secondary/20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
          <h2 className="text-2xl font-bold text-foreground">What severe service actually means</h2>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            Most drivers qualify. Severe service is short trips under 10 miles, stop-and-go
            commuting, towing, roof or bed loads, mountain grades, or ambient temperatures above
            90°F. Fluid degrades on heat cycles rather than distance, which is why a 40,000-mile
            city car can have worse fluid than an 80,000-mile highway car.
          </p>
          <h3 className="mt-8 text-xl font-semibold text-foreground">Symptoms, ranked by how late you are</h3>
          <dl className="mt-4 divide-y divide-border/60">
            {SIGNS.map(([sign, detail]) => (
              <div key={sign} className="py-3">
                <dt className="font-medium text-foreground">{sign}</dt>
                <dd className="mt-1 text-sm text-muted-foreground">{detail}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        <h2 className="text-2xl font-bold text-foreground">Your model's exact interval</h2>
        <p className="mt-3 max-w-3xl text-muted-foreground">
          The figures above are by transmission type. The interval on your car also moves with
          engine size, drivetrain, tow rating and brand service practice — pick a make for the
          per-model schedule.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {makes.slice(0, 36).map((m: { slug: string; name: string }) => (
            <Link
              key={m.slug}
              to="/$make/maintenance-schedule"
              params={{ make: m.slug }}
              className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary"
            >
              {m.name}
            </Link>
          ))}
        </div>
        <Link to="/car-maintenance-schedule" className="cta-gold mt-6">
          Full car maintenance schedule by mileage
        </Link>
      </section>

      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
          <FaqBlock items={FAQS} />
        </div>
      </section>
    </div>
  );
}
